import { Col as AntColumn, Row as AntRow } from "antd";
import {
  default as AntSpace,
  SpaceProps as AntSpaceProps,
} from "antd/lib/space";

import React from "react";
import styled, { css } from "styled-components";

import { white, veryWhiteGrey } from "~/Color";
import * as Shadow from "~/Shadow";
import * as Typography from "~/Typography";

export namespace Page {
  export const padding = () => "2em" as const;
  export const Component = styled.div<Spacing.Props>`
    top: 0;
    left: 0;
    right: 0;
    position: absolute;
    box-sizing: border-box;
    ${(props) => Spacing.styles(Spacing.padding())({ all: padding() })(props)}

    min-width: calc(max(1000px, 100%));

    font-size: ${() => Typography.FontSize.base()};
  `;
}

export namespace Block {
  export const padding = () => "1em" as const;
  export const Component = styled.div<Spacing.Props>`
    ${(props) => Spacing.styles(Spacing.padding())({ all: padding() })(props)}
  `;
}

export namespace Inline {
  export const padding = () => "0.4em" as const;
  export const Component = styled.span<Spacing.Props>`
    ${(props) => Spacing.styles(Spacing.padding())({ all: padding() })(props)}
  `;
}

export function Space({
  children,
  ...props
}: AntSpaceProps & { readonly children?: React.ReactNode }) {
  return <AntSpace {...props}>{children}</AntSpace>;
}

export namespace Row {
  export const Component = AntRow;
}

export namespace Column {
  export const Component = AntColumn;
}

export namespace Section {
  export const padding = () => Page.padding();
  export const Component = styled.div<Spacing.Props>`
    position: relative;
    box-sizing: border-box;

    ${(props) => Spacing.styles(Spacing.padding())({ all: padding() })(props)}

    background: ${() => white()};
    ${() => Border.styles()}
    ${Shadow.Box.styles(Shadow.large())};
  `;

  export namespace Border {
    export const style = (): Style => `1px solid ${veryWhiteGrey()}`;
    export const styles = () => css`
      border: ${style};
      ${Radius.styles};
    `;

    export namespace Radius {
      export const style = (): Style => "0.5em";
      export const styles = () => css`
        border-radius: ${style};
      `;
    }
  }

  export namespace Container {
    export const padding = (): string => Page.padding();
    export const Component = styled.div<Spacing.Props>`
      ${(props) => Spacing.styles(Spacing.padding())({ all: padding() })(props)}
    `;
  }

  export namespace Header {
    const height = () => "3.5em";

    export const Component: Component<{
      readonly Left?: Component;
      readonly Right?: Component;
      readonly children?: Component.Children;
    }> = ({ Left = () => null, Right = () => null, children }) => (
      <Container.Component left right>
        <div>
          <Left />
        </div>
        <div>{children}</div>
        <div>
          <Right />
        </div>
      </Container.Component>
    );

    namespace Container {
      export const Component = styled(Block.Component)`
        display: flex;
        border-bottom: 1px solid ${veryWhiteGrey};

        & > div {
          flex: 1;
          display: flex;
          justify-content: center;
          align-items: center;
          height: ${height};
        }

        & > div:first-of-type {
          justify-content: flex-start;
          align-items: center;
        }

        & > div:last-of-type {
          justify-content: flex-end;
          align-items: center;
        }
      `;
    }

    export const Title = ({
      children,
    }: {
      readonly children?: Component.Children;
    }) => (
      <Typography.H2.Component all={false}>{children}</Typography.H2.Component>
    );
  }
}

export namespace Spacing {
  export type Spacing = ReturnType<
    typeof Spacing.margin | typeof Spacing.padding
  >;

  export type Props = {
    readonly all?: boolean;
    readonly left?: boolean;
    readonly right?: boolean;
    readonly top?: boolean;
    readonly bottom?: boolean;
  };

  export type Amount = {
    readonly all?: string;
    readonly left?: string;
    readonly right?: string;
    readonly top?: string;
    readonly bottom?: string;
  };

  export const margin = () => "margin" as const;
  export const padding = () => "padding" as const;

  export const styles = (spacing: Spacing) => ({
    all: allAmount = "0",
    left: leftAmount = allAmount,
    right: rightAmount = allAmount,
    top: topAmount = allAmount,
    bottom: bottomAmount = allAmount,
  }: Amount) => ({
    all: applyAll = false,
    left: applyLeft = applyAll,
    right: applyRight = applyAll,
    top: applyTop = applyAll,
    bottom: applyBottom = applyAll,
  }: Props): ReturnType<typeof css> =>
    css`
      ${spacing}-left: ${applyLeft ? leftAmount : 0};
      ${spacing}-right: ${applyRight ? rightAmount : 0};
      ${spacing}-top: ${applyTop ? topAmount : 0};
      ${spacing}-bottom: ${applyBottom ? bottomAmount : 0};
    `;
}

export namespace Flex {
  type Props = Spacing.Props & {
    readonly row?: boolean;
    readonly justify?:
      | "center"
      | "flex-start"
      | "flex-end"
      | "space-between"
      | "space-around"
      | "stretch";
    readonly align?:
      | "center"
      | "flex-start"
      | "flex-end"
      | "space-between"
      | "space-around"
      | "stretch";
    readonly bordered?: boolean;
  };

  export const Component = styled.span<Props>`
    display: flex;
    ${(props) => `flex-direction: ${props.row ? "row" : "column"};`}
    ${(props) =>
      `justify-content: ${props.justify ? props.justify : "center"};`}
    ${(props) => `align-items: ${props.align ? props.align : "center"};`}

    ${(props) =>
      Spacing.styles(Spacing.padding())({ all: Inline.padding() })(props)}
    ${(props) =>
      `border: ${
        props.bordered ? 1 : 0
      }px solid ${veryWhiteGrey()}; border-radius: 6px;`}
  `;
}
